import { useEffect, useState } from "react";
import AccountButton from "../AccountButton";
import AccountInput from "../AccountInput";
import AccountSection from "../AccountSection";
import AccountTextarea from "../AccountTextarea";
import {
  addMedicalProcedure,
  deleteMedicalProcedure,
  getMedicalProcedures,
  updateMedicalProcedure,
} from "../api/apiFunctions";
import { t } from "../i18n";
import { getAuthToken } from "../utils/auth";
import { replaceNullWithPlaceholder } from "../utils";
import { useSubscriptionAccess } from "../context/SubscriptionAccessContext";

export default function ProceduresPage({ lang = "en", goToTab }) {
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    procedureName: "",
    procedureType: "",
    code: "",
    // codeDotted: "",
    details: "",
    procedureDate: "",
  });
  const [procedures, setProcedures] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    type: "",
    code: "",
    // codeDotted: "",
    details: "",
    procedureDate: "",
    status: "active",
  });
  const { dontHaveAccess } = useSubscriptionAccess();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [procedureToDelete, setProcedureToDelete] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadProcedures();
  }, []);

  // Helper function to extract procedures data from API response
  const extractProceduresData = (response) => {
    try {
      if (Array.isArray(response)) {
        return response;
      }
      if (
        response &&
        typeof response === "object" &&
        response.success &&
        response.data
      ) {
        if (
          response.data.procedures &&
          Array.isArray(response.data.procedures)
        ) {
          return response.data.procedures;
        } else if (Array.isArray(response.data)) {
          return response.data;
        } else {
          return [];
        }
      }
      if (response && typeof response === "object" && response.data) {
        if (
          response.data.procedures &&
          Array.isArray(response.data.procedures)
        ) {
          return response.data.procedures;
        } else if (Array.isArray(response.data)) {
          return response.data;
        } else {
          return [];
        }
      }
      if (response && typeof response === "object") {
        return [];
      }
      return [];
    } catch (error) {
      console.error("Error extracting procedures data:", error);
      return [];
    }
  };

  // Load procedures from API
  const loadProcedures = async () => {
    try {
      setIsLoading(true);
      setError("");

      const token = getAuthToken();
      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      const response = await getMedicalProcedures(token);
      const proceduresData = extractProceduresData(response);

      // Transform data to match expected format
      const transformedProcedures = proceduresData.map((procedure) => {
        return {
          id: procedure._id || procedure.id,
          procedureName: procedure.procedureName || "",
          procedureType: procedure.procedureType || "",
          code: procedure.medicalCode || procedure.code || "",
          // codeDotted: procedure.dotted ? "Yes" : "No",
          details: procedure.description || procedure.details || "",
          procedureDate: procedure.procedureDate || procedure.date || "",
          status: procedure.status || "active",
        };
      });

      setProcedures(transformedProcedures);
    } catch (error) {
      console.error("Error loading procedures:", error);
      setError(error.message || "Failed to load procedures. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const statusOptions = [
    { value: "all", label: t("allStatuses", lang) },
    { value: "active", label: t("active", lang) },
    { value: "inactive", label: t("inactive", lang) },
  ];

  // Handlers
  const handleSearchChange = (e) => setSearch(e.target.value);
  const handleStatusChange = (e) => setStatusFilter(e.target.value);
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveProcedure = async () => {
    const procedureName = form.procedureName.trim();

    if (!procedureName) {
      setError("Please enter a procedure name");
      return false;
    }

    try {
      setIsSaving(true);
      setError("");
      setSuccessMessage("");

      const token = getAuthToken();
      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      const procedureData = {
        procedureName: procedureName,
        procedureType: form.procedureType.trim(),
        description: form.details.trim(),
        medicalCode: form.code.trim(),
        // dotted:
        //   form.codeDotted.trim().toLowerCase() === "yes" ||
        //   form.codeDotted.trim().toLowerCase() === "true",
        procedureDate: form.procedureDate || null,
      };

      const response = await addMedicalProcedure(procedureData, token);

      if (response.success) {
        // Add the new procedure to the local state
        const newProcedure = {
          id: response.data._id || response.data.id,
          procedureName: procedureName,
          procedureType: form.procedureType.trim(),
          code: form.code.trim(),
          // codeDotted: form.codeDotted.trim() || "No",
          details: form.details.trim(),
          procedureDate: form.procedureDate || "",
          status: "active",
        };
        setProcedures((prev) => [...prev, newProcedure]);

        // Clear the form
        setForm({
          procedureName: "",
          procedureType: "",
          code: "",
          // codeDotted: "",
          details: "",
          procedureDate: "",
        });
        setSuccessMessage("Procedure added successfully!");

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(""), 3000);
        return true; // Return true to indicate success
      } else {
        throw new Error(response.message || "Failed to add procedure");
      }
    } catch (error) {
      console.error("Error adding procedure:", error);
      setError(error.message || "Failed to add procedure. Please try again.");
      return false; // Return false to indicate failure
    } finally {
      setIsSaving(false);
    }
  };

  const removeProcedure = (index) => {
    const procedure = procedures[index];
    setProcedureToDelete({ index, procedure });
    setShowDeleteConfirm(true);
  };

  const confirmDeleteProcedure = async () => {
    if (!procedureToDelete) return;

    try {
      setIsSaving(true);
      setError("");
      setSuccessMessage("");

      const token = getAuthToken();
      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      const response = await deleteMedicalProcedure(
        procedureToDelete.procedure.id,
        token,
      );

      if (response.success) {
        // Remove from local state
        setProcedures((prev) =>
          prev.filter((_, i) => i !== procedureToDelete.index),
        );
        setSuccessMessage("Procedure deleted successfully!");

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        throw new Error(response.message || "Failed to delete procedure");
      }
    } catch (error) {
      console.error("Error deleting procedure:", error);
      setError(
        error.message || "Failed to delete procedure. Please try again.",
      );
    } finally {
      setIsSaving(false);
      setShowDeleteConfirm(false);
      setProcedureToDelete(null);
    }
  };

  const startEdit = (index) => {
    const procedure = procedures[index];
    setEditingIndex(index);
    setEditForm({
      name: procedure.procedureName || "",
      type: procedure.procedureType || "",
      code: procedure.code || "",
      // codeDotted: procedure.codeDotted || "",
      details: procedure.details || "",
      procedureDate: procedure.procedureDate || "",
      status: procedure.status,
    });
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditForm({
      name: "",
      type: "",
      code: "",
      // codeDotted: "",
      details: "",
      status: "active",
    });
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) {
      setError("Please enter a procedure name");
      return;
    }

    try {
      setIsSaving(true);
      setError("");
      setSuccessMessage("");

      const token = getAuthToken();
      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      const procedure = procedures[editingIndex];

      if (!procedure.id) {
        throw new Error("Try refreshing the page.");
      }

      const procedureData = {
        procedureName: editForm.name.trim(),
        procedureType: editForm.type.trim(),
        description: editForm.details.trim(),
        medicalCode: editForm.code.trim(),
        // dotted:
        //   editForm.codeDotted.trim().toLowerCase() === "yes" ||
        //   editForm.codeDotted.trim().toLowerCase() === "true",
        procedureDate: editForm.procedureDate || null,
      };

      const response = await updateMedicalProcedure(
        procedure.id,
        procedureData,
        token,
      );

      if (response.success) {
        // Update the local state
        setProcedures((prev) =>
          prev.map((procedure, index) =>
            index === editingIndex
              ? {
                  ...procedure,
                  procedureName: editForm.name.trim(),
                  procedureType: editForm.type.trim(),
                  code: editForm.code.trim(),
                  // codeDotted: editForm.codeDotted.trim(),
                  details: editForm.details.trim(),
                  procedureDate: editForm.procedureDate,
                  status: editForm.status,
                }
              : procedure,
          ),
        );
        setSuccessMessage("Procedure updated successfully!");

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(""), 3000);
        cancelEdit();
      } else {
        throw new Error(response.message || "Failed to update procedure");
      }
    } catch (error) {
      console.error("Error updating procedure:", error);
      setError(
        error.message || "Failed to update procedure. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  // Filtered procedures
  const filteredProcedures = procedures.filter((proc) => {
    const matchesSearch = proc.procedureName
      ?.toLowerCase()
      .includes(search.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || proc.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-[#f7f8fa] flex flex-col items-center p-4 md:p-8">
      <div className="w-full max-w-4xl">
        <div className="bg-white rounded-xl shadow p-8 mt-4">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-red-400 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-green-400 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-green-700 text-sm">{successMessage}</span>
              </div>
            </div>
          )}

          {/* Medical Procedures */}
          <AccountSection>{t("medicalProcedures", lang)}</AccountSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {/* {t("searchProcedures", lang)} */}
                Add Procedures
              </label>
              <div className="relative flex items-center mb-5">
                <input
                  type="text"
                  name="procedureName"
                  disabled={dontHaveAccess}
                  value={form.procedureName}
                  onChange={handleFormChange}
                  placeholder={t("searchProceduresPlaceholder", lang)}
                  className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                />
              </div>
              <AccountInput
                label="Type of Procedure"
                name="procedureType"
                value={form.procedureType}
                disabled={dontHaveAccess}
                onChange={handleFormChange}
              />
              <div className="mb-6" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <AccountInput
                  label={t("medicalCode", lang)}
                  name="code"
                  value={form.code}
                  onChange={handleFormChange}
                  disabled={dontHaveAccess}
                />
                {/* <AccountInput
                  label={t("medicalCodeDotted", lang)}
                  name="codeDotted"
                  value={form.codeDotted}
                  onChange={handleFormChange}
                /> */}
              </div>
              <AccountInput
                label={"Date"}
                name="procedureDate"
                type="date"
                value={form.procedureDate}
                onChange={handleFormChange}
                disabled={dontHaveAccess}
              />
            </div>
            <div>
              <AccountTextarea
                label={t("detailsNotes", lang)}
                name="details"
                value={form.details}
                onChange={handleFormChange}
                disabled={dontHaveAccess}
                rows={7}
                placeholder={t("addNotesDetails", lang)}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <AccountButton
              className="md:w-1/3"
              disabled={isSaving}
              onClick={() => goToTab("prev")}
            >
              <span className="mr-2">&laquo;</span> {t("back", lang)}
            </AccountButton>
            <AccountButton
              className="md:w-1/3"
              onClick={() => saveProcedure()}
              disabled={isSaving || dontHaveAccess}
            >
              {isSaving ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </div>
              ) : (
                t("save", lang)
              )}
            </AccountButton>
            <AccountButton
              className="md:w-1/3"
              onClick={() => goToTab("next")}
              disabled={isSaving}
            >
              <>
                {t("next", lang)} <span className="ml-2">&raquo;</span>
              </>
            </AccountButton>
          </div>

          {/* My Procedures Table */}
          <AccountSection>{t("myProcedures", lang)}</AccountSection>
          <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
            <div className="flex-1 flex items-center gap-2">
              <input
                id="procedure-search"
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder={t("searchPlaceholder", lang)}
                className="border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                style={{ maxWidth: 200 }}
              />
              <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-[#f7f8fa] border border-gray-200">
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  stroke="#837f90"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
              </span>
            </div>
            <select
              value={statusFilter}
              onChange={handleStatusChange}
              className="border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
              style={{ maxWidth: 140 }}
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="overflow-x-auto max-w-xs sm:max-w-full">
            <table className="bg-white text-sm min-w-full">
              <thead>
                <tr className="bg-[#f7f8fa] text-[#837f90]">
                  <th className="px-4 py-2 text-left font-semibold">
                    Name of Procedure
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">
                    Type of Procedure
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("medicalCode", lang)}
                  </th>
                  {/* <th className="px-4 py-2 text-left font-semibold">
                    {t("dotted", lang)}
                  </th> */}
                  <th className="px-4 py-2 text-left font-semibold">Date</th>
                  <th className="px-4 py-2 text-left font-semibold">Details</th>
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("status", lang)}
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("actions", lang)}
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-3 h-6 w-6 text-[#4962d8]"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span className="text-[#837f90]">
                          Loading procedures...
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : filteredProcedures.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-[#837f90]">
                      <div className="flex flex-col items-center">
                        <svg
                          className="w-12 h-12 text-gray-300 mb-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1"
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <p className="text-lg font-medium mb-2">
                          No procedures added yet
                        </p>
                        <p className="text-sm text-gray-500">
                          Add your first procedure using the form above
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredProcedures.map((proc, idx) => {
                    const actualIndex = procedures.findIndex((p) => p === proc);
                    const isEditing = editingIndex === actualIndex;

                    return (
                      <tr key={idx} className="border-t border-gray-100">
                        {isEditing ? (
                          <>
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                name="name"
                                value={editForm.name}
                                onChange={handleEditFormChange}
                                disabled={dontHaveAccess}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                                placeholder={t("Procedure Name", lang)}
                              />
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                name="type"
                                value={editForm.type}
                                disabled={dontHaveAccess}
                                onChange={handleEditFormChange}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                                placeholder={t("Procedure Type", lang)}
                              />
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                name="code"
                                value={editForm.code}
                                onChange={handleEditFormChange}
                                disabled={dontHaveAccess}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                                placeholder={t("medicalCode", lang)}
                              />
                            </td>
                            {/* <td className="px-4 py-2">
                              <input
                                type="text"
                                name="codeDotted"
                                value={editForm.codeDotted}
                                onChange={handleEditFormChange}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                                placeholder={t("medicalCodeDotted", lang)}
                              />
                            </td> */}
                            <td className="px-4 py-2">
                              <input
                                type="date"
                                name="procedureDate"
                                value={editForm.procedureDate}
                                disabled={dontHaveAccess}
                                onChange={handleEditFormChange}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                name="details"
                                value={editForm.details}
                                onChange={handleEditFormChange}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <select
                                name="status"
                                value={editForm.status}
                                disabled={dontHaveAccess}
                                onChange={handleEditFormChange}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                              >
                                <option value="active">Current</option>
                                <option value="inactive">Past</option>
                              </select>
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={saveEdit}
                                  disabled={isSaving || dontHaveAccess}
                                  className="text-green-500 hover:text-green-700 p-1 rounded hover:bg-green-50"
                                  title={t("save", lang)}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                  >
                                    <polyline points="9,11 12,14 22,4" />
                                    <path d="M21,12v7a2,2,0,0,1-2,2H5a2,2,0,0,1-2-2V5a2,2,0,0,1,2-2h11" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEdit}
                                  className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-50"
                                  title={t("cancel", lang)}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                  >
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-2">
                              {replaceNullWithPlaceholder(proc.procedureName)}
                            </td>
                            <td className="px-4 py-2">
                              {replaceNullWithPlaceholder(proc.procedureType)}
                            </td>
                            <td className="px-4 py-2">
                              {replaceNullWithPlaceholder(proc.code)}
                            </td>
                            {/* <td className="px-4 py-2">
                               {replaceNullWithPlaceholder(proc.codeDotted)}
                            </td> */}
                            <td className="px-4 py-2">
                              {replaceNullWithPlaceholder(proc.procedureDate)}
                            </td>
                            <td className="px-4 py-2">
                              {replaceNullWithPlaceholder(proc.details)}
                            </td>
                            <td className="px-4 py-2">
                              <span
                                className={`inline-block px-3 py-1 text-xs rounded-full ${
                                  proc.status === "active"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                                }`}
                              >
                                {t(
                                  proc.status === "active" ? "Current" : "Past",
                                  lang,
                                )}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => startEdit(actualIndex)}
                                  className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50"
                                  title={t("edit", lang)}
                                  disabled={dontHaveAccess}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeProcedure(actualIndex)}
                                  className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                                  title={t("delete", lang)}
                                  disabled={dontHaveAccess}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                  >
                                    <polyline points="3,6 5,6 21,6" />
                                    <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6" />
                                    <line x1="10" y1="11" x2="10" y2="17" />
                                    <line x1="14" y1="11" x2="14" y2="17" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Delete Procedure
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete "
                {procedureToDelete?.procedure?.procedureName}"? This action
                cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setProcedureToDelete(null);
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteProcedure}
                  disabled={isSaving}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSaving ? (
                    <div className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Deleting...
                    </div>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
